import React, { useEffect } from 'react';
import { hasWhenAvailable, normalizeReminders } from '../../../../../utils/missionReminders';
import { OPEN_WAIT_REMINDER } from '../../../../../utils/waitFor';
import RemindMeCluster from './components/RemindMeCluster';
import { useRemindMePicker } from './hooks/useRemindMePicker';
import { formatStartWhen } from './utils/reminderLabels';
import './RemindMeRow.css';

const stop = (e) => e.stopPropagation();

/**
 * Compact "Remind me" control for mission cards and the sidebar.
 */
export default function RemindMeRow({
  task,
  onRemindersChange,
  isTemplate = false,
  isLocked = false,
  variant = 'card',
  classPrefix = 'mission-card',
  prompt,
}) {
  const reminders = normalizeReminders(task?.reminders);
  const canEdit = Boolean(onRemindersChange) && !isTemplate;
  const showTimed = !isLocked;
  const hasStartReminder = reminders.some((reminder) => reminder.forStart);
  const lockedDone = isLocked && hasWhenAvailable(reminders) && hasStartReminder;
  const canAdd = canEdit && !lockedDone && (isLocked || showTimed);
  const startMs = task?.startedAt ? Date.parse(task.startedAt) : NaN;
  const startIsFuture = Number.isFinite(startMs) && startMs > Date.now();
  const startLabel = startIsFuture ? formatStartWhen(task.startedAt) : null;
  const persist = (next) => onRemindersChange?.(task.id, next);

  const picker = useRemindMePicker({
    taskId: task?.id,
    reminders,
    canEdit,
    isLocked,
    startMs,
    startIsFuture,
    hasStartReminder,
    persist,
  });

  useEffect(() => {
    if (!task?.id || !canEdit) return undefined;
    const open = () => picker.openAdd?.();
    const onAsk = (event) => {
      if (event.detail?.taskId === task.id) open();
    };
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(OPEN_WAIT_REMINDER) === task.id) {
      sessionStorage.removeItem(OPEN_WAIT_REMINDER);
      open();
    }
    window.addEventListener(OPEN_WAIT_REMINDER, onAsk);
    return () => window.removeEventListener(OPEN_WAIT_REMINDER, onAsk);
  }, [task?.id, canEdit, picker]);

  if ((!canEdit && reminders.length === 0) || (canEdit && !isLocked && !showTimed && reminders.length === 0)) {
    return null;
  }

  const cluster = (
    <RemindMeCluster
      className={variant === 'sidebar' ? 'remind-me remind-me--sidebar' : 'remind-me'}
      reminders={reminders}
      canEdit={canEdit}
      showAdd={canAdd}
      addLabel={prompt ? (reminders.length ? 'Add' : 'Set') : (reminders.length ? 'Add' : 'Remind me')}
      addPlaceholder={variant !== 'sidebar' && reminders.length === 0}
      hasStartReminder={hasStartReminder}
      startLabel={startLabel}
      persist={persist}
      picker={picker}
    />
  );

  if (variant === 'sidebar') {
    return (
      <div className="mfv-metadata-bar__field mfv-metadata-bar__field--remind">
        <span className="mfv-metadata-bar__label">Remind</span>
        {cluster}
      </div>
    );
  }

  return (
    <div className={`${classPrefix}__date-row remind-me-row`} onMouseDown={stop}>
      <span className={`${classPrefix}__date-row-label`}>{prompt || 'Remind'}</span>
      <div className={`${classPrefix}__date-row-value`}>
        {cluster}
      </div>
    </div>
  );
}
